import { Box, CardContent, Grid, Stack } from "@mui/material";
import { MprcButton, MprcCard, MprcContainer, MprcFormSubHeader, MprcRequestErrorReport, MprcRequestIndicator, MprcSpinnerIndicator, MprcTypography } from "components";
import { ButtonCancel } from "components/buttons";
import { MprcPinCodeField } from "components/MprcPinCodeField";
import { TwoFactorEmailValidationResult } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SessionService } from "services/sessionService";
interface IProps {
    onClose: (result: "cancel" | "validaded") => void;
}

const INITIAL_TIME_SECONDS = 60;

export const TwoFactorEmailAuthenticator: React.FC<IProps> = (props) => {
    const { t } = useTranslation();
    const { onClose } = props;
    const [otp, setOtp] = useState('');
    const [sending, setSending] = useState(true);
    const [checking, setChecking] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [validationText, setValidationText] = useState<string | null>(null);
    const [viewError, setViewError] = useState<any>(null);
    const [time, setTime] = useState(0);
    const api = useCoreApi();
    const session = useSession();

    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    ///////////////////////////////////////////////////////////////////////////

    ///
    const startTimer = useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current);

        setTime(INITIAL_TIME_SECONDS);

        timerRef.current = setInterval(() => {
            setTime((prevTime) => {
                if (prevTime <= 1) {
                    clearTimer();
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);
    }, []);

    ///
    const clearTimer = () => {
        if (timerRef.current) clearInterval(timerRef.current);
    };

    ///
    const handleSendCode = async () => {
        setSending(true);
        setEmailSent(false);

        try {
            var response = await api.v1.account.emailAuthenticatorSendCode({});
            setEmailSent(response.data);
        }
        catch (error) {
            setViewError(error);
        }
        finally {
            setSending(false);
        }
    };

    ///
    const handleChange = (newValue: string) => {
        if (!emailSent) setOtp("");
        else setOtp(newValue);
    };

    const handleComplete = async (pinCode: string) => {
        setChecking(true);
        setValidationText(null);
        try {
            var response = await api.v1.account.emailAuthenticatorValidateCode({ pinCode });
            if (response.data === TwoFactorEmailValidationResult.Match) {
                onClose("validaded");
            }
            else {
                if (response.data === TwoFactorEmailValidationResult.Expired) setValidationText("Auth.Login.2FA.Email.ExpiredText");
                else setValidationText(t("Auth.Login.2FA.Email.InvalidCodeText"));
                setOtp("");
            }
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setChecking(false);
        }
    };

    ///////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (emailSent) startTimer();
        else clearTimer();
    }, [emailSent]);

    useEffect(() => {
        handleSendCode();
        return clearTimer();
    }, []);

    ///////////////////////////////////////////////////////////////////////////

    return (
        <Grid container direction="column" justifyContent="center" alignItems="center" spacing={2} >
            <Grid size="grow">
                <img
                    src={SessionService.current.appImages.highlightImageUrl}
                    width="360"
                />
            </Grid>
            <Grid size="grow">
                <MprcCard sx={{ maxWidth: 450 }}>
                    <CardContent>
                        <MprcRequestIndicator type={"post"} visible={checking} />

                        {viewError
                            ? <MprcRequestErrorReport error={viewError} />
                            : <div>
                                <MprcFormSubHeader
                                    title={
                                        <center>
                                            <Box sx={{ minHeight: 32 }}>
                                                {t("Auth.Login.2FA.Email.Title")}
                                            </Box>
                                        </center>
                                    }
                                    subtitle={
                                        <center>
                                            {t("Auth.Login.2FA.Email.Subtitle")}
                                            <br />
                                            ({LibUtils.maskEmail(session.user.email!)})
                                        </center>
                                    }
                                    margin="dense"
                                />

                                <center>
                                    <Box sx={{ mt: 4, mb: 4, width: "85%" }}>
                                        <MprcPinCodeField
                                            autoFocus
                                            isNumeric
                                            length={6}
                                            value={otp}
                                            onComplete={handleComplete}
                                            onChange={handleChange}
                                        />
                                    </Box>

                                    {validationText &&
                                        <MprcContainer>
                                            <MprcTypography variant="captionHeader" color="error">
                                                {validationText}
                                            </MprcTypography>
                                        </MprcContainer>
                                    }
                                </center>

                                <Stack justifyContent={"space-between"} alignItems={"center"} direction={"row"}>
                                    <MprcButton
                                        variant="contained"
                                        color={!sending && !emailSent ? "error" : "muted"}
                                        disabled={sending || time > 0}
                                        onClick={handleSendCode}
                                    >
                                        {sending
                                            ? t("Auth.Login.2FA.Email.SendingEmail")
                                            : emailSent
                                                ? t("Auth.Login.2FA.Email.ResendEmail")
                                                : t("Auth.Login.2FA.Email.RetryEmail")
                                        }
                                        {!sending && time > 0 && ` (${time})`}
                                        {sending && <MprcSpinnerIndicator />}
                                    </MprcButton>
                                    <ButtonCancel onClick={() => onClose("cancel")} />
                                </Stack>

                            </div>
                        }
                    </CardContent>
                </MprcCard>
            </Grid>
        </Grid>
    );

};